import { createI18n } from 'vue-i18n';
import { nextTick, ref, customRef } from 'vue';
import { createLocaleLoader } from './loader.js';
import { locales, isLocale, preferredLocale, readLocaleCookie, localeCookie, messageKey } from './preferences.js';

export { locales };
export const i18n = createI18n({ legacy: false, locale: 'cn', fallbackLocale: false, messages: {}, missingWarn: false, fallbackWarn: false });
export const languageChoice = ref('system');
export const languageBusy = ref(false);
const loaders = {
  cn: () => Promise.all([import('./locales/cn.json'), import('vuetify/lib/locale/zh-Hans.js')]),
  'zh-Hant': () => Promise.all([import('./locales/zh-Hant.json'), import('vuetify/lib/locale/zh-Hant.js')]),
  en: () => Promise.all([import('./locales/en.json'), import('vuetify/lib/locale/en.js')]),
  es: () => Promise.all([import('./locales/es.json'), import('vuetify/lib/locale/es.js')]),
};
const loadLocale = createLocaleLoader(loaders, busy => { languageBusy.value = busy; });
let vuetifyLocale;
export const tr = (source, params = {}) => {
  if (typeof source !== 'string') return source == null ? '' : String(source);
  const key = `ui.${messageKey(source)}`;
  return i18n.global.te(key) ? i18n.global.t(key, params) : source;
};
// 只调整展示语言，不改变时间戳、时区归属、币种或业务金额。
export const localeTag = () => locales[i18n.global.locale.value].tag;
export const formatUiDate = (value, options = { dateStyle: 'short', timeStyle: 'short' }) => {
  if (value == null || value === '') return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : new Intl.DateTimeFormat(localeTag(), options).format(date);
};
// 仅用于开发者维护的列标题和固定选项；业务数据仍按原值展示。
export const trHeaders = headers => headers.map(header => ({ ...header, title: tr(header.title) }));
export const trOptions = options => options.map(option => typeof option === 'string'
  ? { title: tr(option), value: option }
  : { ...option, ...(option.title == null ? { label: tr(option.label) } : { title: tr(option.title) }) });
const UI_MESSAGE = Symbol('uiMessage');
// 保留前端提示的原文及参数，读取时按当前语言渲染；后端字符串不经过翻译。
export const uiMessage = (source, params = {}) => ({ [UI_MESSAGE]: true, source, params });
export function useUiMessage(initial = '') {
  let value = initial;
  return customRef((track, trigger) => ({
    get() {
      track();
      const current = typeof value === 'function' ? value() : value;
      return current?.[UI_MESSAGE]
        ? tr(current.source, typeof current.params === 'function' ? current.params() : current.params)
        : current;
    },
    set(next) { value = next; trigger(); },
  }));
}
export function bindVuetifyLocale(locale) { vuetifyLocale = locale; }
const languages = () => navigator.languages?.length ? navigator.languages : [navigator.language];

export async function setLanguage(choice, { persist = true } = {}) {
  if (choice !== 'system' && !isLocale(choice)) throw new Error('Unsupported locale');
  const target = preferredLocale(choice, languages());
  return loadLocale(target, async resources => {
    i18n.global.setLocaleMessage(target, resources[0].default);
    if (vuetifyLocale) {
      vuetifyLocale.messages.value[target] = resources[1].default;
      vuetifyLocale.current.value = target;
    }
    i18n.global.locale.value = target;
    languageChoice.value = choice;
    document.documentElement.lang = locales[target].tag;
    if (persist) document.cookie = localeCookie(choice, location.hostname, location.protocol === 'https:');
    await nextTick();
  });
}
export async function initializeLanguage() {
  const saved = readLocaleCookie(document.cookie);
  try { await setLanguage(saved || 'system', { persist: false }); }
  catch { await setLanguage('cn', { persist: false }); }
}
export function synchronizeLanguage() {
  const saved = readLocaleCookie(document.cookie);
  const choice = saved || 'system';
  if (languageBusy.value) return;
  if (choice !== languageChoice.value || preferredLocale(saved, languages()) !== i18n.global.locale.value) {
    // 后台切换失败时保留当前语言和全部工作状态。
    return setLanguage(choice, { persist: false }).catch(() => {});
  }
}
